/**
 * Edit Limit dialog — Access `frmReportExposureQryLimitAdd` (caption header
 * "Update Exposure Limit"). btnEdit loads the chosen row by `code`, sets
 * limit_date = Now() and a blank note; Save INSERTs a NEW row into
 * report_exposure_limit (is_approved auto server-side).
 *
 * Layout 1:1 Access — single column (all inputs Left=2592), order by Top:
 *   code (CP Code, readonly) · name (CP Name, readonly) · limit_date (Limited
 *   Date) · quantity_limit_mt (Limited Quantity (MT), Standard) ·
 *   amount_limit_usd (Limited Amount (USD), Standard) · limit_note (Note).
 * btnSave/btnCancel live in the Access FormHeader (Top=852, right) → DialogTitle.
 */
import { Box, Dialog, DialogContent, TextField } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import DialogTitleBar from '../../../components/DialogTitleBar'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { type Dayjs } from 'dayjs'
import { useEffect, useState } from 'react'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { useCreateExposureLimitMutation } from '../../../store/api/dashboardApi'

interface Props {
  open: boolean
  code: string | null
  name: string | null
  /** Existing limit on the selected row — Access btnEdit `Grant_Record_Value` pre-fills these. */
  quantityLimitMt?: number | null
  amountLimitUsd?: number | null
  limitNote?: string | null
  onClose: () => void
  onSaved: () => void
}

const numericInputProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }

const readonlyFieldSx: SxProps<Theme> = {
  '& .MuiInputBase-root': {
    bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5'),
  },
  '& .MuiInputBase-input': {
    color: (theme) => theme.palette.text.secondary,
    fontWeight: 600,
  },
}

function parseNumeric(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

// Limited Quantity (MT) / Limited Amount (USD) display with 0 decimals (chị Đẹp).
function formatStandard(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const n = typeof v === 'number' ? v : parseNumeric(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

export default function ExposureLimitEditDialog({
  open,
  code,
  name,
  quantityLimitMt,
  amountLimitUsd,
  limitNote,
  onClose,
  onSaved,
}: Props) {
  const [limitDate, setLimitDate] = useState<Dayjs | null>(dayjs())
  const [quantity, setQuantity] = useState('')
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [create, { isLoading: saving }] = useCreateExposureLimitMutation()
  const snack = useSnackbar()

  // Mirror Access btnEdit: Grant_Record_Value pre-fills the row's existing
  // quantity/amount, then limit_date = Now() and note is blanked.
  useEffect(() => {
    if (!open) return
    setLimitDate(dayjs())
    setQuantity(formatStandard(quantityLimitMt))
    setAmount(formatStandard(amountLimitUsd))
    setNote(limitNote ?? '')
  }, [open, quantityLimitMt, amountLimitUsd, limitNote])

  const handleClose = () => {
    if (saving) return
    onClose()
  }

  const handleSave = async () => {
    if (saving || !code) return
    if (!limitDate) {
      snack.error('Please choose Limited Date!')
      return
    }
    const qty = parseNumeric(quantity)
    const amt = parseNumeric(amount)
    if (!qty) {
      snack.error('Please type Limited Quantity (MT)!')
      return
    }
    if (!amt) {
      snack.error('Please type Limited Amount (USD)!')
      return
    }
    try {
      await create({
        code,
        limitDate: limitDate.format('YYYY-MM-DD'),
        quantityLimitMt: qty,
        amountLimitUsd: amt,
        limitNote: note || null,
      }).unwrap()
      snack.success('Successfully Updated')
      onSaved()
      onClose()
    } catch (err: unknown) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail
      snack.error(detail ?? 'Could not save exposure limit.')
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (saving || reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitleBar
        title="Update Exposure Limit"
        onSave={handleSave}
        onCancel={handleClose}
        saving={saving}
        saveDisabled={!code || saving}
      />
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 0.5 }}>
            <TextField
              label="CP Code"
              value={code ?? ''}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={readonlyFieldSx}
            />
            <TextField
              label="CP Name"
              value={name ?? ''}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={readonlyFieldSx}
            />
            <DatePicker
              label="Limited Date"
              format="DD-MMM-YYYY"
              value={limitDate}
              onChange={setLimitDate}
              slotProps={{
                textField: { size: 'small', fullWidth: true, required: true, InputLabelProps: { shrink: true } },
              }}
            />
            <TextField
              label="Limited Quantity (MT)"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              onBlur={() => setQuantity((v) => formatStandard(v))}
              size="small"
              type="text"
              required
              fullWidth
              inputProps={numericInputProps}
            />
            <TextField
              label="Limited Amount (USD)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onBlur={() => setAmount((v) => formatStandard(v))}
              size="small"
              type="text"
              required
              fullWidth
              inputProps={numericInputProps}
            />
            <TextField
              label="Note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        </LocalizationProvider>
      </DialogContent>
    </Dialog>
  )
}
